/**
 * Global ARIA live-region announcer (axe: status-msg-not-auto-announced, WCAG 4.1.3).
 *
 * Many status messages in the app (toasts, loading spinners, success/empty banners,
 * "no matching options") mount together with their text or are toggled with v-show,
 * so an in-place `aria-live` region is not reliably re-read by screen readers. This
 * module keeps two persistent, visually-hidden live regions appended to <body> and
 * pushes text into them, which screen readers announce dependably.
 *
 * Usage:
 *   import { announce } from "@/composables/useLiveAnnouncer"
 *   announce("Successfully created.")                  // polite
 *   announce("Something went wrong.", { assertive: true }) // assertive
 */

let politeRegion = null
let assertiveRegion = null

/* Messages are queued and flushed one at a time so that rapid consecutive
   announcements (e.g. "Loading…" immediately followed by a results summary) are
   each committed to the DOM as a distinct change, instead of one cancelling the
   other. A polite live region then reads them in order. */
const queue = []
let flushing = false
let flushTimer = null

/* Time to leave a region empty before writing the next message (so the change is
   detected) and the minimum spacing between messages. Exported so tests can assert
   on behavior without hardcoding these magic numbers. */
export const CLEAR_MS = 60
export const GAP_MS = 250

const HIDDEN_STYLE = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: "0",
  border: "0",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap"
}

const createRegion = (liveness) => {
  const el = document.createElement("div")
  el.setAttribute("aria-live", liveness)
  el.setAttribute("aria-atomic", "true")
  el.setAttribute("role", liveness === "assertive" ? "alert" : "status")
  el.setAttribute("data-live-announcer", liveness)
  Object.assign(el.style, HIDDEN_STYLE)
  document.body.appendChild(el)
  return el
}

/** Lazily create (or re-attach) the live regions. Returns false in non-DOM envs. */
const ensureRegions = () => {
  if (typeof document === "undefined" || !document.body) {
    return false
  }
  if (!politeRegion || !document.body.contains(politeRegion)) {
    politeRegion = createRegion("polite")
  }
  if (!assertiveRegion || !document.body.contains(assertiveRegion)) {
    assertiveRegion = createRegion("assertive")
  }
  return true
}

const flushNext = () => {
  if (!queue.length) {
    flushing = false
    return
  }
  flushing = true
  const { text, assertive } = queue.shift()
  const region = assertive ? assertiveRegion : politeRegion
  if (!region) {
    flushing = false
    return
  }
  /* Clear first so an identical or repeated message is still detected as a change,
     then write the text, then wait a beat before the next queued message. */
  region.textContent = ""
  flushTimer = setTimeout(() => {
    region.textContent = text
    flushTimer = setTimeout(flushNext, GAP_MS)
  }, CLEAR_MS)
}

/**
 * Announce a status message to screen readers via a persistent live region.
 *
 * @param {string} message - Plain text to announce. Empty/whitespace is ignored.
 * @param {{ assertive?: boolean }} [options] - `assertive: true` for errors/warnings.
 */
export const announce = (message, options = {}) => {
  const text = typeof message === "string" ? message.trim() : ""
  if (!text) {
    return
  }
  if (!ensureRegions()) {
    return
  }

  const assertive = !!options.assertive

  /* Skip if the same message is already the most recent in the queue (avoids
     duplicate spam when a watcher fires several times for one update). */
  const last = queue[queue.length - 1]
  if (last && last.text === text && last.assertive === assertive) {
    return
  }

  /* Keep the queue short; only the latest few status messages matter. */
  if (queue.length > 8) {
    queue.shift()
  }

  queue.push({ text, assertive })
  if (!flushing) {
    flushNext()
  }
}

/**
 * Eagerly create the live regions so they exist in the DOM before the first message.
 * Screen readers only announce changes to live regions that were already present, so
 * call this once at app startup to avoid the first announcement being dropped.
 */
export const initLiveAnnouncer = () => ensureRegions()

/** Test helper: remove regions and reset module state. */
export const __resetLiveAnnouncer = () => {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  queue.length = 0
  flushing = false
  ;[politeRegion, assertiveRegion].forEach((el) => {
    if (el && el.parentNode) {
      el.parentNode.removeChild(el)
    }
  })
  politeRegion = null
  assertiveRegion = null
}

export default { announce }
